import { parseISO } from 'date-fns'
import { useEffect, useState } from 'react'
import { getSecondsRemaining } from './utils'

/**
 * Whether `expiresAt` has passed, re-evaluated when it does and whenever the
 * session is prolonged.
 *
 * Deliberately not {@link useCountdown}: this drives the frame around the entire
 * app, and a value that changed every second would re-render the whole shell
 * that often for a border that changes once. Between renders the clock is read
 * once, which can only ever overestimate the time left — so the frame never
 * escalates early — and expiry itself is scheduled off a fresh clock.
 */
export const useHasSessionExpired = (
  expiresAt: string | undefined,
): boolean => {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    if (!expiresAt) {
      return
    }

    const millisecondsUntilExpiry = parseISO(expiresAt).getTime() - Date.now()

    // Already expired: re-read the clock so a stale `now` cannot keep the frame
    // calm, and stop — there is no later state to schedule.
    if (millisecondsUntilExpiry <= 0) {
      setNow(new Date())
      return
    }

    const timeoutId = setTimeout(() => {
      setNow(new Date())
    }, millisecondsUntilExpiry)

    return () => clearTimeout(timeoutId)
  }, [expiresAt])

  if (!expiresAt) {
    return false
  }

  return getSecondsRemaining(expiresAt, now) <= 0
}
